import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { HeroSlidesManager } from "@/components/admin/HeroSlidesManager";
import { WebsiteTab } from "@/components/admin/website/WebsiteTab";
import { PdfThumbnail } from "@/components/PdfThumbnail";
import { 
  GraduationCap, 
  Users,
  BookOpen,
  ClipboardCheck,
  Plus,
  LogOut,
  User,
  Trash2,
  Shield,
  Mail,
  Lock,
  Presentation,
  KeyRound,
  ArrowRightLeft,
  Upload,
  Image,
  Film,
  Trash2 as TrashIcon,
  Play,
  X as XIcon,
  ExternalLink
} from "lucide-react";
import { motion } from "framer-motion";
import { SiteContentTab } from "@/components/admin/SiteContentTab";
import { BannersTab } from "@/components/admin/BannersTab";
import { CourseContentTab } from "@/components/admin/CourseContentTab";
import { CmsHub } from "@/components/admin/CmsHub";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [allQuizzes, setAllQuizzes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [activeTab, setActiveTab] = useState(() => (new URLSearchParams(window.location.search).get("gcal") ? "website" : "teachers"));
  const [creating, setCreating] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [newTeacher, setNewTeacher] = useState({
    name: "",
    email: "",
    password: ""
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teachersRes, statsRes, studentsRes, classesRes, quizzesRes, heroRes] = await Promise.all([
        axios.get(`${API}/admin/teachers`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/students`, { headers }),
        axios.get(`${API}/admin/classes`, { headers }),
        axios.get(`${API}/admin/quizzes`, { headers }),
        axios.get(`${API}/settings/homepage`).catch(() => ({ data: { hero_image: "/hero.jpg" } }))
      ]);
      
      setTeachers(teachersRes.data);
      setStats(statsRes.data);
      setStudents(studentsRes.data);
      setGallery(heroRes.data.gallery || []);
      setClasses(classesRes.data);
      setAllQuizzes(quizzesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };




  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    let uploaded = 0;
    for (const file of files) {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      const isPdf = file.type === "application/pdf";
      if (!isVideo && !isImage && !isPdf) {
        toast.error(`Skipped "${file.name}" — only images, videos and PDFs allowed`);
        continue;
      }
      const maxMb = isVideo ? 50 : isPdf ? 25 : 10;
      if (file.size > maxMb * 1024 * 1024) {
        toast.error(`Skipped "${file.name}" — over ${maxMb}MB`);
        continue;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await axios.post(`${API}/admin/homepage-gallery`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        const media = res.data.media;
        if (media.url && !media.url.startsWith("http") && !media.url.startsWith("/api/")) {
          media.url = `/api/homepage-media/${media.url}`;
        }
        setGallery((prev) => [...prev, media]);
        uploaded++;
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to upload "${file.name}"`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} media item(s) uploaded!`);
    setUploadingGallery(false);
    e.target.value = "";
  };

  const handleDeleteGalleryItem = async (mediaId) => {
    if (!confirm("Remove this media from the homepage?")) return;
    try {
      await axios.delete(`${API}/admin/homepage-gallery/${mediaId}`, { headers });
      setGallery((prev) => prev.filter((item) => item.id !== mediaId));
      toast.success("Media removed");
    } catch (error) {
      toast.error("Failed to remove media");
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setGallery((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };

  const handleDragEnd = async () => {
    setDragIdx(null);
    try {
      await axios.put(`${API}/admin/homepage-gallery/reorder`, { order: gallery.map((g) => g.id) }, { headers });
    } catch {
      toast.error("Failed to save order");
    }
  };

  const handleCreateTeacher = async () => {
    if (!newTeacher.name || !newTeacher.email || !newTeacher.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/admin/teachers/create`, newTeacher, { headers });
      toast.success("Teacher account created! A welcome email has been sent.");
      setNewTeacher({ name: "", email: "", password: "" });
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create teacher");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTeacher = async (userId, teacherName) => {
    if (!confirm(`Are you sure you want to delete ${teacherName}'s account? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/teachers/${userId}`, { headers });
      toast.success("Teacher account deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete teacher");
    }
  };

  const handleResetPassword = async (userId, userName, role) => {
    const newPassword = prompt(`Enter new password for ${userName}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    try {
      await axios.put(`${API}/admin/users/${userId}/reset-password`, 
        { new_password: newPassword }, 
        { headers }
      );
      toast.success(`Password reset for ${userName}. New password: ${newPassword}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleDeleteStudent = async (userId, studentName) => {
    if (!confirm(`Are you sure you want to delete ${studentName}'s account? This will remove them from all classes.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/students/${userId}`, { headers });
      toast.success("Student account deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete student");
    }
  };

  const openTransferDialog = (teacher) => {
    setTransferFrom(teacher);
    setTransferTo("");
    setTransferDialogOpen(true);
  };

  const handleTransferClasses = async () => {
    if (!transferTo) {
      toast.error("Please select a destination teacher");
      return;
    }
    setTransferring(true);
    try {
      const res = await axios.post(`${API}/admin/transfer-classes`, {
        from_teacher_id: transferFrom.user_id,
        to_teacher_id: transferTo
      }, { headers });
      toast.success(res.data.message);
      setTransferDialogOpen(false);
      setTransferFrom(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer classes");
    } finally {
      setTransferring(false);
    }
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewTeacher({ ...newTeacher, password });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="TheMatheMind 360" className="h-9 sm:h-10 rounded-lg" />
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold text-foreground font-['Manrope'] leading-tight whitespace-nowrap">TheMatheMind <span className="text-amber-600">360</span></span>
                <span className="text-[10px] text-muted-foreground leading-tight tracking-wider uppercase">A 360&deg; Learning Platform</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">Admin</span>
              </div>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground font-['Manrope']">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Manage your academy's teachers and monitor platform usage</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
            <Card className="border-border/50 cursor-pointer hover:border-blue-300 transition-colors" onClick={() => { setActiveTab("teachers"); document.getElementById('teachers-students-tabs')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="stat-teachers">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Presentation className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_teachers || 0}</p>
                    <p className="text-xs text-muted-foreground">Teachers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border/50 cursor-pointer hover:border-green-300 transition-colors" onClick={() => { setActiveTab("students"); document.getElementById('teachers-students-tabs')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="stat-students">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_students || 0}</p>
                    <p className="text-xs text-muted-foreground">Students</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-border/50 cursor-pointer hover:border-primary/50 transition-colors" onClick={() => { setActiveTab("classes"); document.getElementById('teachers-students-tabs')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="stat-classes">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_classes || 0}</p>
                    <p className="text-xs text-muted-foreground">Classes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-border/50 cursor-pointer hover:border-orange-300 transition-colors" onClick={() => { setActiveTab("quizzes"); document.getElementById('teachers-students-tabs')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="stat-quizzes">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_quizzes || 0}</p>
                    <p className="text-xs text-muted-foreground">Quizzes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Teachers & Students Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" id="teachers-students-tabs">
          <TabsList className="flex flex-wrap w-full h-auto gap-1 mb-4">
            <TabsTrigger value="cms" className="flex-1 min-w-[90px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-cms">
              CMS
            </TabsTrigger>
            <TabsTrigger value="website" className="flex-1 min-w-[90px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-website">
              Website
            </TabsTrigger>
            <TabsTrigger value="teachers" className="flex-1 min-w-[90px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Presentation className="w-4 h-4 mr-2" />
              Teachers ({teachers.length})
            </TabsTrigger>
            <TabsTrigger value="students" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="w-4 h-4 mr-2" />
              Students ({students.length})
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-classes">
              <BookOpen className="w-4 h-4 mr-2" />
              Classes ({classes.length})
            </TabsTrigger>
            <TabsTrigger value="quizzes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-quizzes">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Quizzes ({allQuizzes.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-settings">
              Settings
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-content">
              Content
            </TabsTrigger>
            <TabsTrigger value="banners" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-banners">
              Banners
            </TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-courses">
              Courses
            </TabsTrigger>
          </TabsList>

          {/* Teachers Tab */}
          <TabsContent value="teachers">
            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="font-['Manrope']">Teachers</CardTitle>
                  <CardDescription>Manage teacher accounts for your academy</CardDescription>
                </div>
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-teacher-btn" className="rounded-full bg-primary text-primary-foreground">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Teacher
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">Create Teacher Account</DialogTitle>
                      <DialogDescription>Add a new teacher to your academy</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label htmlFor="teacher-name">Full Name *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="teacher-name-input"
                            id="teacher-name"
                            placeholder="John Doe"
                            className="pl-10"
                            value={newTeacher.name}
                            onChange={(e) => setNewTeacher({ ...newTeacher, name: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="teacher-email">Email *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="teacher-email-input"
                            id="teacher-email"
                            type="email"
                            placeholder="teacher@academy.com"
                            className="pl-10"
                            value={newTeacher.email}
                            onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="teacher-password">Password *</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={generatePassword}
                            className="text-xs h-auto py-1"
                          >
                            Generate
                          </Button>
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="teacher-password-input"
                            id="teacher-password"
                            type="text"
                            placeholder="Enter password"
                            className="pl-10"
                            value={newTeacher.password}
                            onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })}
                          />
                        </div>
                        {newTeacher.password && (
                          <p className="text-xs text-muted-foreground">
                            Share these credentials with the teacher securely
                          </p>
                        )}
                      </div>
                      <Button
                        data-testid="submit-create-teacher"
                        onClick={handleCreateTeacher}
                        className="w-full rounded-full bg-primary text-primary-foreground"
                        disabled={creating}
                      >
                        {creating ? "Creating..." : "Create Teacher Account"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {teachers.length === 0 ? (
                  <div className="text-center py-12">
                    <Presentation className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-lg text-muted-foreground">No teachers yet</p>
                    <p className="text-sm text-muted-foreground mb-4">Add your first teacher to get started</p>
                    <Button
                      onClick={() => setCreateDialogOpen(true)}
                      className="rounded-full bg-primary text-primary-foreground"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Teacher
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teachers.map((teacher) => (
                      <motion.div
                        key={teacher.user_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 p-4 rounded-xl border border-border/50 group hover:border-primary/30 transition-all"
                        data-testid={`teacher-${teacher.user_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-primary font-medium">
                              {teacher.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">{teacher.name}</p>
                            <p className="text-sm text-muted-foreground truncate">{teacher.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-auto opacity-100 sm:opacity-0 sm:group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-blue-600"
                            onClick={() => openTransferDialog(teacher)}
                            data-testid={`transfer-classes-${teacher.user_id}`}
                            title="Transfer Classes"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-primary"
                            onClick={() => handleResetPassword(teacher.user_id, teacher.name, 'teacher')}
                            data-testid={`reset-password-teacher-${teacher.user_id}`}
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteTeacher(teacher.user_id, teacher.name)}
                            data-testid={`delete-teacher-${teacher.user_id}`}
                            title="Delete Teacher"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Students</CardTitle>
                <CardDescription>View and manage all student accounts (created by teachers)</CardDescription>
              </CardHeader>
              <CardContent>
                {students.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-lg text-muted-foreground">No students yet</p>
                    <p className="text-sm text-muted-foreground">Teachers will create student accounts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {students.map((student) => (
                      <motion.div
                        key={student.user_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center justify-between p-4 rounded-xl border border-border/50 group hover:border-primary/30 transition-all"
                        data-testid={`student-${student.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 font-medium">
                              {student.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{student.name}</p>
                            <p className="text-sm text-muted-foreground">{student.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-primary"
                            onClick={() => handleResetPassword(student.user_id, student.name, 'student')}
                            data-testid={`reset-password-student-${student.user_id}`}
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteStudent(student.user_id, student.name)}
                            data-testid={`delete-student-${student.user_id}`}
                            title="Delete Student"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Classes</CardTitle>
                <CardDescription>All classes across the platform</CardDescription>
              </CardHeader>
              <CardContent>
                {classes.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-lg text-muted-foreground">No classes yet</p>
                    <p className="text-sm text-muted-foreground">Teachers will create classes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {classes.map((cls) => (
                      <motion.div
                        key={cls.class_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center justify-between p-4 rounded-xl border border-border/50 group hover:border-primary/30 transition-all"
                        data-testid={`class-${cls.class_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{cls.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {cls.subject} · {cls.teacher_name} · {cls.student_count} student(s)
                            </p>
                            <p className="text-xs text-muted-foreground">Code: {cls.join_code}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quizzes Tab */}
          <TabsContent value="quizzes">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Quizzes</CardTitle>
                <CardDescription>All quizzes across the platform</CardDescription>
              </CardHeader>
              <CardContent>
                {allQuizzes.length === 0 ? (
                  <div className="text-center py-12">
                    <ClipboardCheck className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-lg text-muted-foreground">No quizzes yet</p>
                    <p className="text-sm text-muted-foreground">Teachers will create quizzes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allQuizzes.map((quiz) => (
                      <motion.div
                        key={quiz.quiz_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center justify-between p-4 rounded-xl border border-border/50 group hover:border-primary/30 transition-all"
                        data-testid={`quiz-${quiz.quiz_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <ClipboardCheck className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{quiz.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {quiz.teacher_name} · {quiz.question_count} questions · {quiz.total_points} pts
                            </p>
                            {quiz.is_timed && (
                              <p className="text-xs text-orange-600">{quiz.time_limit_minutes} min time limit</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="content">
            <SiteContentTab />
          </TabsContent>

          <TabsContent value="banners">
            <BannersTab />
          </TabsContent>

          <TabsContent value="website">
            <WebsiteTab />
          </TabsContent>

          <TabsContent value="cms">
            <CmsHub onGoToTab={setActiveTab} />
          </TabsContent>

          <TabsContent value="courses">
            <CourseContentTab />
          </TabsContent>

          <TabsContent value="settings">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Homepage Settings</CardTitle>
                <CardDescription>Manage the hero carousel and media gallery on the landing page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <HeroSlidesManager headers={headers} />

                {/* Divider */}
                <div className="border-t border-border" />

                {/* Media Gallery Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Film className="w-4 h-4" /> Media Gallery ({gallery.length} items)
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Add images and videos that appear below the hero image on the homepage. Visitors can click to view fullscreen.
                  </p>

                  {/* Upload Area */}
                  <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,application/pdf"
                      multiple
                      onChange={handleGalleryUpload}
                      className="hidden"
                      id="gallery-upload"
                      data-testid="gallery-upload-input"
                    />
                    <label htmlFor="gallery-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-1">
                        {uploadingGallery ? "Uploading..." : "Click to add images, videos or PDFs"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Images: JPG, PNG, WebP (max 10MB) &middot; Videos: MP4, WebM (max 50MB) &middot; PDF (max 25MB). Select multiple files at once.
                      </p>
                    </label>
                  </div>

                  {/* Gallery Grid */}
                  {gallery.length > 0 ? (
                    <>
                      <p className="text-xs text-muted-foreground italic">Drag items to reorder. Order is saved automatically.</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="admin-gallery-grid">
                        {gallery.map((item, idx) => (
                          <div
                            key={item.id}
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDragEnd={handleDragEnd}
                            className={`relative rounded-xl overflow-hidden border bg-muted/20 aspect-video group cursor-grab active:cursor-grabbing transition-all ${dragIdx === idx ? "border-primary ring-2 ring-primary/30 opacity-70 scale-95" : "border-border"}`}
                            data-testid={`admin-gallery-item-${item.id}`}
                          >
                            {item.type === "video" ? (
                              <>
                                <video
                                  src={item.url}
                                  className="w-full h-full object-cover pointer-events-none"
                                  muted
                                  preload="metadata"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                  <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                                    <Play className="w-4 h-4 text-foreground ml-0.5" />
                                  </div>
                                </div>
                              </>
                            ) : item.type === "pdf" ? (
                              <PdfThumbnail url={item.url} filename={item.filename} />
                            ) : (
                              <img
                                src={item.url}
                                alt={item.filename || "Gallery image"}
                                className="w-full h-full object-cover pointer-events-none"
                              />
                            )}
                            {/* Delete overlay */}
                            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {item.type === "pdf" && (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-colors"
                                  title="Open PDF in new tab"
                                  data-testid={`open-gallery-${item.id}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5 text-foreground" />
                                </a>
                              )}
                              <button
                                className="w-7 h-7 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-lg transition-colors"
                                onClick={() => handleDeleteGalleryItem(item.id)}
                                data-testid={`delete-gallery-${item.id}`}
                                title="Remove"
                              >
                                <XIcon className="w-3.5 h-3.5 text-white" />
                              </button>
                            </div>
                            {/* Order badge + Type badge */}
                            <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/80 text-white font-medium">
                                {idx + 1}
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white uppercase tracking-wide">
                                {item.type}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Film className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No gallery media yet. Upload images or videos above.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        {/* Info Box */}
        <Card className="border-primary/20 bg-primary/5 mt-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Access Control</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Only you (admin) can create teacher accounts. Teachers can then create student accounts 
                  and add them to their classes. Public registration is disabled for security.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transfer Classes Dialog */}
        <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                Transfer Classes
              </DialogTitle>
              <DialogDescription>
                Transfer all classes from <strong>{transferFrom?.name}</strong> to another teacher. 
                Students, assignments, quizzes, and all content will be preserved.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">From</p>
                <p className="font-medium">{transferFrom?.name}</p>
                <p className="text-sm text-muted-foreground">{transferFrom?.email}</p>
              </div>
              
              <div className="flex justify-center">
                <ArrowRightLeft className="w-5 h-5 text-muted-foreground rotate-90" />
              </div>

              <div className="space-y-2">
                <Label>Transfer to</Label>
                <select
                  data-testid="transfer-to-select"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                >
                  <option value="">Select a teacher...</option>
                  {teachers
                    .filter(t => t.user_id !== transferFrom?.user_id)
                    .map(t => (
                      <option key={t.user_id} value={t.user_id}>
                        {t.name} ({t.email})
                      </option>
                    ))
                  }
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setTransferDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-transfer-btn"
                  className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleTransferClasses}
                  disabled={!transferTo || transferring}
                >
                  {transferring ? "Transferring..." : "Transfer Classes"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
